import {Card, Modal, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {GET_CARRIER_APPLICANTS_DISTRIBUTION} from '../../../gql/queries/reportsQuery'

interface ICarrierApplicantsData {
  carrierName: string
  applicantsCount: number
  percentage: number
}

interface IResCarrierApplicants {
  carrierApplicantsDistribution: ICarrierApplicantsData[]
}

interface ICarrierApplicantsTableProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  refreshTrigger?: number
}

type SortField = 'carrier' | 'applicants'
type SortOrder = 'asc' | 'desc'

export const CarrierApplicantsTable = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  refreshTrigger,
}: ICarrierApplicantsTableProps) => {
  const [showModal, setShowModal] = useState(false)
  const [sortField, setSortField] = useState<SortField>('applicants')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getCarrierData, {data, loading}] = useLazyQuery<IResCarrierApplicants>(
    GET_CARRIER_APPLICANTS_DISTRIBUTION,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [getAllCarrierData, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResCarrierApplicants>(GET_CARRIER_APPLICANTS_DISTRIBUTION, {
      fetchPolicy: 'no-cache',
    })

  useEffect(() => {
    getCarrierData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, paymentStatus, advisorPaid, refreshTrigger, getCarrierData])

  const handleShowMore = () => {
    getAllCarrierData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 100,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'applicants' ? 'desc' : 'asc')
    }
  }

  const carrierData = data?.carrierApplicantsDistribution || []
  const fullCarrierData = fullData?.carrierApplicantsDistribution || []

  const sortedFullCarrierData = [...fullCarrierData].sort((a, b) => {
    if (sortField === 'carrier') {
      const comparison = a.carrierName.localeCompare(b.carrierName)
      return sortOrder === 'asc' ? comparison : -comparison
    } else {
      const comparison = a.applicantsCount - b.applicantsCount
      return sortOrder === 'asc' ? comparison : -comparison
    }
  })

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Applicants per carrier (Top 10)</span>
            <span className='text-muted fw-semibold fs-7'>
              This is how your applicants are segmented by insurance company
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleShowMore}
              disabled={loading || parentLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              See more
            </button>
          </div>
        </Card.Header>
      <Card.Body className='pt-0'>
        {(loading || parentLoading) && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && !parentLoading && carrierData.length === 0 && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <p className='text-muted'>No data available for the selected period</p>
          </div>
        )}
        {!loading && !parentLoading && carrierData.length > 0 && (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='ps-0' style={{width: '5%'}}></th>
                  <th style={{width: '50%'}}>Insurance company</th>
                  <th className='text-center' style={{width: '25%'}}>Applicants</th>
                  <th className='text-center pe-0' style={{width: '20%'}}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {carrierData.map((entry, index) => (
                  <tr key={entry.carrierName}>
                    <td className='ps-0 text-center'>
                      <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div
                          className='rounded-circle bg-light-info d-flex align-items-center justify-content-center me-3'
                          style={{width: 35, height: 35}}
                        >
                          <i className='bi bi-people text-info fs-5'></i>
                        </div>
                        <span className='text-gray-800 fw-bold fs-6'>{entry.carrierName}</span>
                      </div>
                    </td>
                    <td className='text-center'>
                      <span className='fw-bold text-gray-800 fs-6'>
                        {formatNumber(entry.applicantsCount)}
                      </span>
                    </td>
                    <td className='text-center pe-0'>
                      <span className='badge badge-light-info fs-7'>
                        {entry.percentage.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>

      {/* Modal for all carriers */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Applicants per carrier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullCarrierData.length === 0 && (
            <div className='text-center text-muted py-5'>No data available</div>
          )}
          {!loadingFull && fullCarrierData.length > 0 && (
            <div className='table-responsive' style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0 text-center' style={{width: '5%'}}></th>
                    <th
                      style={{width: '50%', cursor: 'pointer'}}
                      onClick={() => handleSort('carrier')}
                    >
                      Insurance company {getSortIcon('carrier')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '25%', cursor: 'pointer'}}
                      onClick={() => handleSort('applicants')}
                    >
                      Applicants {getSortIcon('applicants')}
                    </th>
                    <th className='text-center pe-0' style={{width: '20%'}}>
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFullCarrierData.map((entry, index) => (
                    <tr key={entry.carrierName}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div
                            className='rounded-circle bg-light-info d-flex align-items-center justify-content-center me-3'
                            style={{width: 35, height: 35}}
                          >
                            <i className='bi bi-people text-info fs-5'></i>
                          </div>
                          <span className='text-gray-800 fw-bold fs-6'>{entry.carrierName}</span>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span className='fw-bold text-gray-800 fs-6'>
                          {formatNumber(entry.applicantsCount)}
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span className='badge badge-light-info fs-7'>
                          {entry.percentage.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
